import React, { useEffect, useState } from 'react';
import { orderAPI } from '../../api/order.api';
import UserDashboardShell from '../../components/user/UserDashboardShell';
import ProductThumbnail from '../../components/product/ProductThumbnail';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatPrice } from '../../utils/formatPrice';
import {
  createDefaultOrders,
  ORDER_STATUS_META,
  readUserDashboardState,
  writeUserDashboardState,
} from '../../utils/userDashboardState';

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'ordered':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.5V12l3 2.5" />
        </svg>
      );
    case 'processing':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 5.5 12 3l6 2.5v8L12 16l-6-2.5Z" />
          <path d="M6 5.5 12 8l6-2.5" />
          <path d="M12 8v8" />
        </svg>
      );
    case 'shipped':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="6" width="16" height="12" rx="2.5" />
          <path d="M4 10h16" />
          <path d="M8 3.5v5" />
          <path d="M16 3.5v5" />
        </svg>
      );
    case 'delivered':
    case 'cancelled':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8.5" />
          <path d="m8.5 12.2 2.2 2.2 4.8-5.1" />
        </svg>
      );
    default:
      return null;
  }
};

const Orders = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState('');

  useEffect(() => {
    if (!user?.id) {
      setOrders([]);
      setHasLoaded(false);
      return;
    }

    const initialOrders = readUserDashboardState('orders', user.id, createDefaultOrders());
    setOrders(initialOrders.filter((order) => order.status !== 'cancelled'));
    setHasLoaded(true);
  }, [user?.id]);

  useEffect(() => {
    if (!hasLoaded || !user?.id) {
      return;
    }

    writeUserDashboardState('orders', user.id, orders);
  }, [hasLoaded, orders, user?.id]);

  const handleTrackOrder = (order) => {
    window.alert(t('orders.trackingSoon', { title: order.title }));
  };

  const handleCancelOrder = async (orderId) => {
    if (!orderId) {
      return;
    }

    if (!user?.id) {
      setOrders((previousOrders) =>
        previousOrders.filter((order) => order.id !== orderId)
      );
      return;
    }

    try {
      setCancellingOrderId(orderId);
      await orderAPI.cancel(orderId);
      setOrders((previousOrders) =>
        previousOrders.filter((order) => order.id !== orderId)
      );
    } catch (error) {
      console.error('Error cancelling order:', error);
      window.alert(error.response?.data?.message || 'Could not cancel the order. Please try again.');
    } finally {
      setCancellingOrderId('');
    }
  };

  return (
    <UserDashboardShell>
      {orders.length === 0 ? (
        <div className="account-empty-state">
          <h3>{t('orders.emptyTitle')}</h3>
          <p>{t('orders.emptyText')}</p>
        </div>
      ) : (
        <section className="account-surface">
          <h2 className="account-page__title">{t('orders.title', { count: orders.length })}</h2>

          <div className="account-orders__list">
            {orders.map((order) => {
              const statusMeta = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.ordered;
              const moreItemsLabel = order.extraItems > 0
                ? order.extraItems === 1 ? t('orders.moreOne') : t('orders.moreMany', { count: order.extraItems })
                : t('orders.oneItem');

              return (
                <article key={order.id} className="account-order">
                  <ProductThumbnail product={order} size="order" className="account-order__art" />

                  <div className="account-order__details">
                    <h3>{order.title}</h3>
                    <p>{moreItemsLabel}</p>
                    {order.specialCode && <p>{t('product.specialCode')}: {order.specialCode}</p>}
                  </div>

                  <div className="account-order__total">{formatPrice(order.total)}</div>

                  <div className="account-order__status">
                    <div
                      className="account-order__status-badge"
                      style={{ backgroundColor: statusMeta.accent }}
                    >
                      <StatusIcon status={order.status} />
                    </div>
                    <span
                      className="account-order__status-label"
                      style={{ color: statusMeta.accent }}
                    >
                      {t(statusMeta.labelKey)}
                    </span>
                  </div>

                  <div className="account-order__actions">
                    <button type="button" className="account-button account-button--primary" onClick={() => handleTrackOrder(order)}>
                      {t('orders.track')}
                    </button>
                    <button
                      type="button"
                      className="account-button account-button--danger"
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={cancellingOrderId === order.id}
                    >
                      {cancellingOrderId === order.id ? 'Canceling...' : t('orders.cancel')}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </UserDashboardShell>
  );
};

export default Orders;
